import React, { useState } from 'react';
import './WorkoutStepEditor.css';

// Helper to extract pace string from either string or object format
const getPaceString = (pace) => {
  if (!pace) return null;
  if (typeof pace === 'string') return pace;
  if (typeof pace === 'object' && pace.pace) return pace.pace;
  return null;
};

// Helper to get the display pace for a zone (handles repetition specially)
const getZonePace = (key, paceData) => {
  if (!paceData) return null;
  // For repetition, show per-km pace instead of 400m time
  if (key === 'repetition' && typeof paceData === 'object') {
    if (paceData.pace_km && paceData.pace_km !== '—') return paceData.pace_km;
  }
  return getPaceString(paceData);
};

const DURATION_TYPES = [
  { value: 'distance', label: 'Distance' },
  { value: 'time', label: 'Time' },
];

// Helper to convert total seconds to hh:mm:ss object
const secondsToHMS = (totalSeconds) => {
  const seconds = totalSeconds || 0;
  return {
    hours: Math.floor(seconds / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: Math.floor(seconds % 60)
  };
};

// Helper to convert hh:mm:ss to total seconds
const hmsToSeconds = (hours, minutes, seconds) => {
  return (parseInt(hours) || 0) * 3600 + (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);
};

const WorkoutStepEditor = ({ steps, onChange, trainingPaces }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingStep, setEditingStep] = useState(null);

  // Helper to create a new step
  const createStep = () => ({
    type: 'step',
    step_type: 'run',
    duration_type: 'distance',
    duration_value: 1.0,
    duration_unit: 'km',
    pace_zone: null,
    notes: ''
  });

  // Helper to create a new repeat block
  const createRepeatBlock = () => ({
    type: 'repeat',
    repeat_count: 2,
    steps: [
      createStep(),
      { ...createStep(), duration_type: 'time', duration_value: 120, duration_unit: 'seconds' } // 2 minutes in seconds
    ]
  });

  const addStep = () => {
    onChange([...steps, createStep()]);
  };

  const addRepeat = () => {
    onChange([...steps, createRepeatBlock()]);
  };

  const removeItem = (index) => {
    const updated = steps.filter((_, i) => i !== index);
    onChange(updated);
  };

  const removeStepFromRepeat = (repeatIndex, stepIndex) => {
    const updated = [...steps];
    updated[repeatIndex].steps = updated[repeatIndex].steps.filter((_, i) => i !== stepIndex);
    // If repeat block has no steps, remove it
    if (updated[repeatIndex].steps.length === 0) {
      updated.splice(repeatIndex, 1);
    }
    onChange(updated);
  };

  const updateRepeatCount = (index, count) => {
    const updated = [...steps];
    updated[index].repeat_count = Math.max(1, Math.min(99, parseInt(count) || 1));
    onChange(updated);
  };

  const startEditing = (index, step, repeatIndex = null) => {
    setEditingIndex({ index, repeatIndex });
    setEditingStep({ ...step });
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditingStep(null);
  };

  const saveEditing = () => {
    if (!editingIndex || !editingStep) return;

    const updated = [...steps];
    if (editingIndex.repeatIndex !== null) {
      updated[editingIndex.repeatIndex].steps[editingIndex.index] = editingStep;
    } else {
      updated[editingIndex.index] = editingStep;
    }
    onChange(updated);
    setEditingIndex(null);
    setEditingStep(null);
  };

  const updateEditingStep = (field, value) => {
    setEditingStep(prev => {
      const updated = { ...prev, [field]: value };

      // Set default units when switching duration type
      if (field === 'duration_type' && value === 'distance') {
        updated.duration_unit = 'km';
        updated.duration_value = updated.duration_value || 1.0;
      }
      if (field === 'duration_type' && value === 'time') {
        updated.duration_unit = 'seconds';
        // Default to 0 seconds when switching to time
        updated.duration_value = 0;
      }

      return updated;
    });
  };

  // Update time component (hours, minutes, or seconds)
  const updateTimeComponent = (component, value) => {
    setEditingStep(prev => {
      const hms = secondsToHMS(prev.duration_value);
      hms[component] = parseInt(value) || 0;
      // Clamp values
      hms.hours = Math.max(0, Math.min(23, hms.hours));
      hms.minutes = Math.max(0, Math.min(59, hms.minutes));
      hms.seconds = Math.max(0, Math.min(59, hms.seconds));
      return {
        ...prev,
        duration_value: hmsToSeconds(hms.hours, hms.minutes, hms.seconds),
        duration_unit: 'seconds'
      };
    });
  };

  // Calculate estimated time based on pace and distance
  const getEstimatedTime = (step) => {
    if (step.duration_type === 'time') {
      // duration_value is already in seconds
      const totalSeconds = step.duration_value || 0;
      return formatTime(totalSeconds);
    }
    if (step.duration_type === 'distance' && step.pace_zone && trainingPaces) {
      const paceStr = getPaceString(trainingPaces[step.pace_zone]);
      if (paceStr) {
        // Parse pace like "5:17" or "5:17-5:57"
        const match = paceStr.match(/(\d+):(\d+)/);
        if (match) {
          const paceSeconds = parseInt(match[1]) * 60 + parseInt(match[2]);
          let distanceKm = step.duration_value || 0;
          // Convert miles to km if needed
          if (step.duration_unit === 'miles') {
            distanceKm = distanceKm * 1.60934;
          }
          const totalSeconds = paceSeconds * distanceKm;
          return formatTime(totalSeconds);
        }
      }
    }
    return null;
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.round(totalSeconds % 60);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get display text for duration
  const getDurationDisplay = (step) => {
    if (step.duration_type === 'distance') {
      const value = step.duration_value?.toFixed(2) || '0.00';
      const unit = step.duration_unit || 'km';
      return { primary: `${value} ${unit}`, secondary: 'Distance' };
    }
    if (step.duration_type === 'time') {
      // duration_value is now in seconds
      const totalSeconds = step.duration_value || 0;
      return { primary: formatTime(totalSeconds), secondary: 'Duration' };
    }
    return { primary: '-', secondary: '' };
  };

  // Get pace zone display
  const getPaceDisplay = (paceZone) => {
    if (!paceZone || !trainingPaces) return null;
    const paceStr = getZonePace(paceZone, trainingPaces[paceZone]);
    if (!paceStr) return null;
    return `${paceZone.toUpperCase()} (${paceStr})`;
  };

  // Calculate totals (all distances converted to km)
  const calculateTotals = () => {
    let totalDistanceKm = 0;
    let totalTimeSeconds = 0;

    const processStep = (step, multiplier = 1) => {
      if (step.duration_type === 'distance' && step.duration_value) {
        let distanceKm = step.duration_value;
        // Convert miles to km if needed
        if (step.duration_unit === 'miles') {
          distanceKm = distanceKm * 1.60934;
        }
        totalDistanceKm += distanceKm * multiplier;
        // Estimate time from pace
        if (step.pace_zone && trainingPaces) {
          const paceStr = getPaceString(trainingPaces[step.pace_zone]);
          if (paceStr) {
            const match = paceStr.match(/(\d+):(\d+)/);
            if (match) {
              const paceSeconds = parseInt(match[1]) * 60 + parseInt(match[2]);
              totalTimeSeconds += paceSeconds * distanceKm * multiplier;
            }
          }
        }
      } else if (step.duration_type === 'time' && step.duration_value) {
        // duration_value is now in seconds
        totalTimeSeconds += step.duration_value * multiplier;
      }
    };

    steps.forEach(item => {
      if (item.type === 'repeat') {
        item.steps.forEach(step => processStep(step, item.repeat_count));
      } else {
        processStep(item);
      }
    });

    return {
      distance: totalDistanceKm.toFixed(2),
      time: totalTimeSeconds > 0 ? formatTime(totalTimeSeconds) : null
    };
  };

  const totals = calculateTotals();

  // Render a single step card
  const renderStepCard = (step, index, repeatIndex = null) => {
    const isEditing = editingIndex &&
      editingIndex.index === index &&
      editingIndex.repeatIndex === repeatIndex;

    const duration = getDurationDisplay(step);
    const estTime = getEstimatedTime(step);

    if (isEditing) {
      return (
        <div key={`${repeatIndex}-${index}`} className="step-card editing">
          <div className="step-card-header">
            <div className="drag-handle">
              <span></span><span></span><span></span>
              <span></span><span></span><span></span>
            </div>
            <span className="step-editing-label">Editing Step...</span>
          </div>

          <div className="step-edit-form">
            <div className="edit-section">
              <h4>Duration</h4>

              <div className="edit-row">
                <label>Type</label>
                <select
                  value={editingStep.duration_type}
                  onChange={(e) => updateEditingStep('duration_type', e.target.value)}
                >
                  {DURATION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {editingStep.duration_type === 'distance' ? (
                <div className="edit-row">
                  <label>Distance</label>
                  <div className="duration-inputs">
                    <input
                      type="number"
                      value={editingStep.duration_value || ''}
                      onChange={(e) => updateEditingStep('duration_value', parseFloat(e.target.value) || 0)}
                      step="0.1"
                      min="0"
                    />
                    <select
                      value={editingStep.duration_unit || 'km'}
                      onChange={(e) => updateEditingStep('duration_unit', e.target.value)}
                    >
                      <option value="km">km</option>
                      <option value="miles">miles</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="edit-row time-row">
                  <label>Time</label>
                  <div className="time-inputs">
                    <input
                      type="number"
                      className="time-input"
                      value={secondsToHMS(editingStep.duration_value).hours}
                      onChange={(e) => updateTimeComponent('hours', e.target.value)}
                      min="0"
                      max="23"
                    />
                    <span className="time-separator">:</span>
                    <input
                      type="number"
                      className="time-input"
                      value={secondsToHMS(editingStep.duration_value).minutes.toString().padStart(2, '0')}
                      onChange={(e) => updateTimeComponent('minutes', e.target.value)}
                      min="0"
                      max="59"
                    />
                    <span className="time-separator">:</span>
                    <input
                      type="number"
                      className="time-input"
                      value={secondsToHMS(editingStep.duration_value).seconds.toString().padStart(2, '0')}
                      onChange={(e) => updateTimeComponent('seconds', e.target.value)}
                      min="0"
                      max="59"
                    />
                  </div>
                  <span className="time-hint">hh:mm:ss</span>
                </div>
              )}
            </div>

            <div className="edit-section">
              <h4>Intensity Target</h4>

              <div className="edit-row">
                <label>Type</label>
                <select
                  value={editingStep.pace_zone || ''}
                  onChange={(e) => updateEditingStep('pace_zone', e.target.value || null)}
                >
                  <option value="">No Target</option>
                  {trainingPaces && Object.entries(trainingPaces).map(([key, pace]) => (
                    <option key={key} value={key}>
                      {key.toUpperCase()} ({getZonePace(key, pace) || '-'})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="edit-section">
              <h4>Notes</h4>
              <textarea
                value={editingStep.notes || ''}
                onChange={(e) => updateEditingStep('notes', e.target.value)}
                placeholder="Add step notes"
                maxLength={200}
              />
              <span className="char-count">{(editingStep.notes || '').length}/200</span>
            </div>

            <div className="edit-actions">
              <button type="button" className="btn-cancel" onClick={cancelEditing}>
                Cancel
              </button>
              <button type="button" className="btn-done" onClick={saveEditing}>
                Done
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={`${repeatIndex}-${index}`} className="step-card run">
        <div className="step-card-header">
          <div className="drag-handle">
            <span></span><span></span><span></span>
            <span></span><span></span><span></span>
          </div>
          <span className="step-type-label">Step</span>
          <div className="step-actions">
            <button
              type="button"
              className="btn-edit-step"
              onClick={() => startEditing(index, step, repeatIndex)}
            >
              Edit Step
            </button>
            <button
              type="button"
              className="btn-delete-step"
              onClick={() => repeatIndex !== null ? removeStepFromRepeat(repeatIndex, index) : removeItem(index)}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="step-card-body">
          <div className="step-stat">
            <span className="stat-value">{duration.primary}</span>
            <span className="stat-label">{duration.secondary}</span>
          </div>

          {estTime && step.duration_type === 'distance' && (
            <div className="step-stat">
              <span className="stat-value">{estTime}</span>
              <span className="stat-label">Est Time</span>
            </div>
          )}

          {step.pace_zone && (
            <div className="step-pace-badge">
              {getPaceDisplay(step.pace_zone)}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render a repeat block
  const renderRepeatBlock = (item, index) => {
    return (
      <div key={index} className="repeat-block">
        <div className="repeat-header">
          <div className="drag-handle">
            <span></span><span></span><span></span>
            <span></span><span></span><span></span>
          </div>
          <span className="repeat-label">Repeat</span>
          <input
            type="number"
            className="repeat-count-input"
            value={item.repeat_count}
            onChange={(e) => updateRepeatCount(index, e.target.value)}
            min="1"
            max="99"
          />
          <span className="repeat-times">Times</span>
          <button
            type="button"
            className="btn-delete-step"
            onClick={() => removeItem(index)}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
          </button>
        </div>

        <div className="repeat-steps">
          {item.steps.map((step, stepIndex) => renderStepCard(step, stepIndex, index))}
        </div>
      </div>
    );
  };

  return (
    <div className="workout-step-editor-v2">
      <div className="steps-toolbar">
        <button type="button" className="btn-add-step" onClick={addStep}>
          + Add Step
        </button>
        <button type="button" className="btn-add-repeat" onClick={addRepeat}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
          </svg>
          Add Repeat
        </button>
      </div>

      <div className="steps-list-v2">
        {steps.length === 0 ? (
          <p className="no-steps-hint">Add steps to build your workout structure</p>
        ) : (
          steps.map((item, index) =>
            item.type === 'repeat'
              ? renderRepeatBlock(item, index)
              : renderStepCard(item, index)
          )
        )}
      </div>

      {steps.length > 0 && (
        <div className="workout-summary">
          {totals.time && (
            <div className="summary-stat">
              <span className="summary-value">{totals.time}</span>
              <span className="summary-label">Est Time</span>
            </div>
          )}
          <div className="summary-stat">
            <span className="summary-value">{totals.distance} km</span>
            <span className="summary-label">Total Distance</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkoutStepEditor;
